import { get, isNil, negate, overEvery, stubTrue, toNumber } from 'lodash/fp';

import {
	FILTER_ABSOLUTE_VALUE_GREATER_THAN_OR_EQUAL,
	FILTER_ABSOLUTE_VALUE_LESS_THAN_OR_EQUAL,
	FILTER_EQUAL,
	FILTER_GREATER_THAN,
	FILTER_GREATER_THAN_OR_EQUAL,
	FILTER_IN,
	FILTER_IS_NULL,
	FILTER_LESS_THAN,
	FILTER_LESS_THAN_OR_EQUAL,
} from './apiUtils';

/**
 * Front-end implementation of filters, useful for mockResolvers and Apollo read field policies.
 * @param {string} filterString - stringified API QueryFilter
 * @param {object} options
 * @property {Function} [extraFilters] - optional extra filter implementation.
 * @property {Function} [readField] - optional function for reading properties from the data, needed for Apollo read
 * 	functions which sometimes reads on ref objects thus special `readField` function must be used.
 * @return {Function} - predicate for data constructed from stringified QueryFilter
 */
export default function matchesFilter(filterString, { extraFilters, readField = get } = {}) {
	if (!filterString) {
		return stubTrue;
	}

	const getNumberValue = (item, property) => toNumber(readField(property, item));
	const getStringValue = (item, property) => String(readField(property, item));

	return overEvery(
		filterString.split(';').map((filter) => {
			const extraPredicate = extraFilters?.(filter);
			if (extraPredicate) {
				return extraPredicate;
			}
			const [property, operator, value] = filter.split(`|`, 3);
			switch (operator) {
				case FILTER_ABSOLUTE_VALUE_LESS_THAN_OR_EQUAL:
					return (item) => Math.abs(getNumberValue(item, property)) <= value;
				case FILTER_ABSOLUTE_VALUE_GREATER_THAN_OR_EQUAL:
					return (item) => Math.abs(getNumberValue(item, property)) >= value;
				case FILTER_EQUAL:
					return (item) => getStringValue(item, property).toLowerCase() === value.toLowerCase();
				case FILTER_IN:
					return (item) => value.split(',').includes(getStringValue(item, property));
				case FILTER_IS_NULL:
					return value === 'true'
						? (item) => isNil(item[property])
						: (item) => negate(isNil)(item[property]);
				case FILTER_LESS_THAN:
					return (item) => getStringValue(item, property) < value;
				case FILTER_GREATER_THAN:
					return (item) => getStringValue(item, property) > value;
				case FILTER_LESS_THAN_OR_EQUAL:
					return (item) => getStringValue(item, property) <= value;
				case FILTER_GREATER_THAN_OR_EQUAL:
					return (item) => getStringValue(item, property) >= value;
				default:
					throw new Error(`Unsupported filter operator "${operator}"`);
			}
		})
	);
}
